import { reactive } from 'vue'
import { request } from './api'
import type { UiCatalog } from './types'
import { asObject, localized, standardBuildingLabel, uiText } from './ui'

const catalogs = reactive<Record<string, UiCatalog | undefined>>({})
const loading = new Map<string, Promise<UiCatalog>>()

export async function worldCatalog(worldId: string): Promise<UiCatalog> {
  const cached = catalogs[worldId]
  if (cached) return cached
  const pending = loading.get(worldId)
  if (pending) return pending
  const promise = request<UiCatalog>(
    `/api/v2/worlds/${worldId}/ui-catalog`,
  ).then(
    (value) => {
      catalogs[worldId] = value
      loading.delete(worldId)
      return value
    },
    (error: unknown) => {
      loading.delete(worldId)
      throw error
    },
  )
  loading.set(worldId, promise)
  return promise
}

export function cachedWorldCatalog(
  worldId: string | undefined,
): UiCatalog | null {
  if (!worldId) return null
  return catalogs[worldId] ?? null
}

export function clearWorldCatalog(worldId?: string): void {
  if (worldId) catalogs[worldId] = undefined
  else for (const key of Object.keys(catalogs)) catalogs[key] = undefined
}

export function buildingLabel(catalog: UiCatalog | null, type: string): string {
  const entry = catalog?.buildings[type]
  return entry
    ? localized(entry.name, standardBuildingLabel(type))
    : standardBuildingLabel(type)
}

export function unitLabel(catalog: UiCatalog | null, key: string): string {
  const definition = asObject(catalog?.units[key])
  return localized(
    definition.name,
    uiText('Unbenannte Einheit', 'Unnamed unit'),
  )
}

export function researchLabel(catalog: UiCatalog | null, key: string): string {
  const research = asObject(catalog?.research)
  const definitions = asObject(
    research.definitions ?? research.research_definitions ?? research.items,
  )
  const definition = asObject(definitions[key])
  return localized(
    definition.name,
    uiText('Unbenannte Forschung', 'Unnamed research'),
  )
}
